import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken, setSuspensionHandler } from "@/lib/api";

const Ctx = createContext(null);
const SHADOW_KEY = "alr_admin_shadow_token";
const IMP_KEY = "alr_impersonation_meta";
const readImp = () => {
  try { return localStorage.getItem(SHADOW_KEY) ? JSON.parse(localStorage.getItem(IMP_KEY)) : null; } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspension, setSuspension] = useState(null);
  const [loginWarnings, setLoginWarnings] = useState([]);
  const [impersonation, setImpersonation] = useState(readImp);

  useEffect(() => {
    setSuspensionHandler((s) => {
      setToken(null);
      setUser(null);
      setSuspension(s);
    });
    (async () => {
      try {
        if (getToken()) setUser(await api("/auth/me"));
      } catch (e) {
        if (e?.status === 401 || e?.status === 403) setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
    return () => setSuspensionHandler(null);
  }, []);

  const login = async (email, password, roleIntent) => {
    const res = await api("/auth/login", {
      method: "POST",
      auth: false,
      body: { email, password, role_intent: roleIntent },
    });
    setToken(res.access_token);
    setUser(res.user);
    setLoginWarnings(Array.isArray(res.warnings) ? res.warnings : []);
    return res.user;
  };

  const loginWithGoogle = useCallback(async (sessionId, roleIntent) => {
    const res = await api("/auth/google", { method: "POST", auth: false, body: { session_id: sessionId, role_intent: roleIntent } });
    setToken(res.access_token);
    setUser(res.user);
    setLoginWarnings(Array.isArray(res.warnings) ? res.warnings : []);
    return res.user;
  }, []);

  const loginWithToken = async (token) => {
    setToken(token);
    const me = await api("/auth/me");
    setUser(me);
    return me;
  };

  const refreshUser = useCallback(async () => {
    try { setUser(await api("/auth/me")); } catch {}
  }, []);

  const logout = () => {
    setToken(null);
    localStorage.removeItem(SHADOW_KEY);
    localStorage.removeItem(IMP_KEY);
    setImpersonation(null);
    setUser(null);
    setLoginWarnings([]);
  };

  const startSupportLogin = async (franchisee) => {
    const res = await api(`/admin/franchisees/${franchisee.id}/support-login`, { method: "POST" });
    localStorage.setItem(SHADOW_KEY, getToken());
    localStorage.setItem(IMP_KEY, JSON.stringify({ name: res.user.full_name || res.user.email, email: res.user.email, started_at: Date.now() }));
    setToken(res.access_token);
  };

  const returnFromImpersonation = async () => {
    const shadow = localStorage.getItem(SHADOW_KEY);
    if (!shadow) return logout();
    setToken(shadow);
    localStorage.removeItem(SHADOW_KEY);
    localStorage.removeItem(IMP_KEY);
    window.location.assign("/admin");
  };

  return (
    <Ctx.Provider
      value={{
        user, loading, login, loginWithGoogle, loginWithToken, logout, refreshUser,
        impersonation, returnFromImpersonation, startSupportLogin,
        suspension, clearSuspension: () => setSuspension(null),
        loginWarnings, clearLoginWarnings: () => setLoginWarnings([]),
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = () => useContext(Ctx);
